/* ======================================================================
   hz-mvs-v2.4.js — Hazendonk Measurement Visual System (FINAL+)
   - Preserves your base parsing + unit toggle + SVG text render approach
   - Supports: outerwear-(short/mid/long), top, bottoms, accessory (scarf/tote_bag)
   - Hides irrelevant layers (closure/neckline/sleeves/garment)
   ====================================================================== */

(function () {

  /* =============================
     HELPERS (UNCHANGED CORE)
  ============================= */

  function toNumber(val) {
    if (val == null) return null;
    const s = String(val).trim();
    if (!s) return null;
    const m = s.replace(",", ".").match(/-?\d+(\.\d+)?/);
    return m ? Number(m[0]) : null;
  }

  function normalizeId(s) {
    return String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-+/g, "_");
  }

  function setVisible(el, show) {
    if (!el) return;
    el.style.display = show ? "" : "none";
  }

  function ensureSvgResponsive(svg) {
    if (!svg) return;
    try {
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    } catch (e) {}
  }

  function showOnlyGroup(svg, parentId, childId) {
    if (!svg) return;
    const parent = svg.querySelector(`#${CSS.escape(parentId)}`);
    if (!parent) return;

    const target = normalizeId(childId);
    if (!target) return;

    // hide all direct children except the matching id
    parent.querySelectorAll(":scope > *").forEach(node => {
      const id = node.getAttribute("id");
      node.style.display = id && normalizeId(id) === target ? "" : "none";
    });
  }

  function showMeasurement(svg, id, show) {
    if (!svg) return;
    const el = svg.querySelector(`#${CSS.escape(id)}`);
    if (el) el.style.display = show ? "" : "none";
  }

  function setSvgText(svg, id, value) {
    if (!svg || value == null) return;
    const el = svg.querySelector(`#${CSS.escape(id)}`);
    if (!el) return;

    const tspans = el.querySelectorAll("tspan");
    if (tspans.length) {
      tspans.forEach(t => (t.textContent = value));
    } else {
      el.textContent = value;
    }
  }

  function forceRecalc() {
    try {
      document.documentElement.offsetHeight;
      window.dispatchEvent(new Event("resize"));
    } catch (e) {}
  }

  /* =============================
     TYPE SUPPORT
  ============================= */

  function getType(root) {
    return String(root?.getAttribute("data-hz-mvs") || "").trim();
  }

  function isSupportedType(type) {
    return (
      type === "outerwear-short" ||
      type === "outerwear-mid" ||
      type === "outerwear-long" ||
      type === "top" ||
      type === "bottom-short" ||
      type === "bottom-three-quarter-pants" ||
      type === "bottom-long" ||
      type === "bottom-dungaree" ||
      type === "bottom-skirt" ||

      // ✅ NEW: accessory wrapper type
      type === "accessory" ||

      // (optional legacy support if you still have old markup somewhere)
      type === "accessory-scarf"
    );
  }

  function getAllRoots() {
    const all = [...document.querySelectorAll("[data-hz-mvs]")];
    return all.filter(el => isSupportedType(getType(el)));
  }

  function getActiveRoot() {
    const roots = getAllRoots();
    if (roots.length === 0) return null;
    if (roots.length === 1) return roots[0];

    let best = null;
    let bestArea = 0;

    roots.forEach(r => {
      const rect = r.getBoundingClientRect();
      const visible =
        rect.width > 50 &&
        rect.height > 50 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight;

      if (!visible) return;

      const area = rect.width * rect.height;
      if (area > bestArea) {
        bestArea = area;
        best = r;
      }
    });

    return best || roots[0];
  }

  /* =============================
     CORE INIT
  ============================= */

  function init(root) {
    if (!root) return;

    // prevent double init
    if (root.__hzMvsInited) return;
    root.__hzMvsInited = true;

    const type = getType(root);

    /* 🔒 BASE VALUES (CM) */
    const base = {
      a: toNumber(root.dataset.a),
      b: toNumber(root.dataset.b),
      c: toNumber(root.dataset.c),
      d: toNumber(root.dataset.d),
    };

    // choice data (outerwear/top)
    const hasChoiceData =
      !!String(root.dataset.neckline || "").trim() ||
      !!String(root.dataset.closure || "").trim();

    // accessory selector
    const accessoryGarment = normalizeId(root.dataset.garment);

    // HARD STOP:
    // - accessory must at least have A or B, otherwise hide
    // - non-accessory: allow render if choices exist even without numbers
    const hasAnyMeasurement =
      base.a !== null ||
      base.b !== null ||
      base.c !== null ||
      base.d !== null;

    if (type === "accessory" || type === "accessory-scarf") {
      const hasAccessoryCore = base.a !== null || base.b !== null;
      if (!hasAccessoryCore) {
        root.style.display = "none";
        return;
      }
    } else {
      if (!hasAnyMeasurement && !hasChoiceData) {
        root.style.display = "none";
        return;
      }
    }

    const svg = root.querySelector('[data-hz-mvs-svg] svg');
    if (!svg) return;

    ensureSvgResponsive(svg);

    let unit = "cm";

    function fmt(n) {
      if (n == null) return null;
      return unit === "in"
        ? `${(n / 2.54).toFixed(1)} in`
        : `${Math.round(n)} cm`;
    }

    /* =============================
       APPLY PER TYPE
    ============================= */

    // common datasets (outerwear/top)
    const sleeveType = normalizeId(root.dataset.sleeveType);
    const sleeveConstruction = normalizeId(root.dataset.sleeveConstruction);
    const neckline = normalizeId(root.dataset.neckline);
    const closure = normalizeId(root.dataset.closure);

    const isRaglan = sleeveConstruction === "raglan";
    const isSetIn = sleeveConstruction === "set_in" || sleeveConstruction === "setin";
    const isSleeveless = sleeveType === "sleeveless";
    const isLongSleeve = sleeveType === "long";
    const isShortSleeve = sleeveType === "short";

    function applySleeveDrawingForOuterwearOrTop(isTop) {
      const sleevesGroup = svg.querySelector("#sleeves");
      if (sleevesGroup) {
        if (isSleeveless) {
          setVisible(sleevesGroup, false);
        } else {
          setVisible(sleevesGroup, true);

          let sleeveLayerId = "";

          if (isLongSleeve) {
            if (isRaglan) sleeveLayerId = "sleeve_type_long_raglan";
            else sleeveLayerId = "sleeve_type_long_set_in";
          } else if (isShortSleeve) {
            if (isTop) {
              if (isRaglan) sleeveLayerId = "sleeve_type_short_raglan";
              else sleeveLayerId = "sleeve_type_short_set_in";
            } else {
              sleeveLayerId = "";
            }
          }

          if (sleeveLayerId) showOnlyGroup(svg, "sleeves", sleeveLayerId);
          else {
            sleevesGroup.querySelectorAll(":scope > *").forEach(node => {
              node.style.display = "none";
            });
          }
        }
      }

      setVisible(svg.querySelector("#base_back"), isRaglan);
      setVisible(svg.querySelector("#base_front"), !isRaglan);
    }

    function applyChoiceGroups() {
      if (neckline) showOnlyGroup(svg, "neckline", neckline);
      if (closure) showOnlyGroup(svg, "closure", closure);

      if (!neckline) {
        const ng = svg.querySelector("#neckline");
        if (ng) ng.querySelectorAll(":scope > *").forEach(n => (n.style.display = "none"));
      }
      if (!closure) {
        const cg = svg.querySelector("#closure");
        if (cg) cg.querySelectorAll(":scope > *").forEach(n => (n.style.display = "none"));
      }
    }

    function applyBottomGarment() {
      const garment = normalizeId(root.dataset.garment);
      const candidates = ["short_pants", "long_pants", "three_quarter_pants", "dungaree", "skirt", "scarf", "tote_bag"];

      candidates.forEach(id => {
        const el = svg.querySelector(`#${CSS.escape(id)}`);
        if (el) el.style.display = (garment && normalizeId(id) === garment) ? "" : "none";
      });
    }

    /* =============================
       OUTERWEAR
    ============================= */

    if (type === "outerwear-short" || type === "outerwear-mid" || type === "outerwear-long") {
      applyChoiceGroups();
      applySleeveDrawingForOuterwearOrTop(false);

      showMeasurement(svg, "length", base.a !== null);
      showMeasurement(svg, "width", base.b !== null);

      showMeasurement(svg, "shoulders_inseam", isLongSleeve && isSetIn && base.c !== null);
      showMeasurement(svg, "sleeve_long_set_in", isLongSleeve && isSetIn && base.d !== null);
      showMeasurement(svg, "sleeve_long_raglan", isLongSleeve && isRaglan && base.c !== null);

      if (!(isLongSleeve && isSetIn)) {
        showMeasurement(svg, "shoulders_inseam", false);
        showMeasurement(svg, "sleeve_long_set_in", false);
      }
      if (!(isLongSleeve && isRaglan)) {
        showMeasurement(svg, "sleeve_long_raglan", false);
      }

      function render() {
        setSvgText(svg, "value_length", fmt(base.a));
        setSvgText(svg, "value_width", fmt(base.b));
        setSvgText(svg, "value_shoulders_inseam", fmt(base.c));
        setSvgText(svg, "value_sleeve_long_set_in", fmt(base.d));
        setSvgText(svg, "value_sleeve_long_raglan", fmt(base.c));
      }

      render();

      root.querySelectorAll("[data-hz-mvs-unit]").forEach(btn => {
        btn.addEventListener("click", () => {
          unit = btn.getAttribute("data-hz-mvs-unit") || "cm";
          root.querySelectorAll("[data-hz-mvs-unit]").forEach(b =>
            b.setAttribute("aria-pressed", b === btn ? "true" : "false")
          );
          render();
          forceRecalc();
        });
      });

      forceRecalc();
      return;
    }

    /* =============================
       TOP
    ============================= */

    if (type === "top") {
      applyChoiceGroups();
      applySleeveDrawingForOuterwearOrTop(true);

      showMeasurement(svg, "length", base.a !== null);
      showMeasurement(svg, "width", base.b !== null);

      const sleeveGroups = [
        "short_sleeve_set_in",
        "long_sleeve_set_in",
        "short_sleeve_raglan",
        "long_sleeve_raglan",
      ];

      sleeveGroups.forEach(id => showMeasurement(svg, id, false));

      const isSetInTop = !isRaglan; // treat blank as set-in
      const showShoulders = !isSleeveless && isSetInTop && base.c !== null;
      showMeasurement(svg, "shoulders_inseam", showShoulders);

      let showSleeveGroupId = null;

      if (!isSleeveless) {
        if (isLongSleeve) {
          if (isRaglan) {
            if (base.c !== null) showSleeveGroupId = "long_sleeve_raglan";
          } else {
            if (base.d !== null) showSleeveGroupId = "long_sleeve_set_in";
          }
        } else if (isShortSleeve) {
          if (isRaglan) {
            if (base.c !== null) showSleeveGroupId = "short_sleeve_raglan";
          } else {
            if (base.d !== null) showSleeveGroupId = "short_sleeve_set_in";
          }
        }
      }

      if (showSleeveGroupId) showMeasurement(svg, showSleeveGroupId, true);

      function render() {
        setSvgText(svg, "value_length", fmt(base.a) || "");
        setSvgText(svg, "value_width", fmt(base.b) || "");
        setSvgText(svg, "value_shoulders_inseam", fmt(base.c) || "");

        setSvgText(svg, "value_sleeve_long_set_in", fmt(base.d) || "");
        setSvgText(svg, "value_sleeve_short_set_in", fmt(base.d) || "");

        setSvgText(svg, "value_sleeve_long_raglan", fmt(base.c) || "");
        setSvgText(svg, "value_sleeve_short_raglan", fmt(base.c) || "");
      }

      render();

      root.querySelectorAll("[data-hz-mvs-unit]").forEach(btn => {
        btn.addEventListener("click", () => {
          unit = btn.getAttribute("data-hz-mvs-unit") || "cm";
          root.querySelectorAll("[data-hz-mvs-unit]").forEach(b =>
            b.setAttribute("aria-pressed", b === btn ? "true" : "false")
          );
          render();
          forceRecalc();
        });
      });

      forceRecalc();
      return;
    }

    /* =============================
       BOTTOMS
    ============================= */

    if (
      type === "bottom-short" ||
      type === "bottom-three-quarter-pants" ||
      type === "bottom-long" ||
      type === "bottom-dungaree" ||
      type === "bottom-skirt"
    ) {
      applyBottomGarment();

      showMeasurement(svg, "length", base.a !== null);
      showMeasurement(svg, "width", base.b !== null);
      showMeasurement(svg, "shoulders_inseam", base.c !== null);

      showMeasurement(svg, "sleeve_hem", base.d !== null);

      function render() {
        setSvgText(svg, "value_length", fmt(base.a));
        setSvgText(svg, "value_width", fmt(base.b));
        setSvgText(svg, "value_shoulders_inseam", fmt(base.c));
        setSvgText(svg, "value_sleeve_hem", fmt(base.d));
      }

      render();

      root.querySelectorAll("[data-hz-mvs-unit]").forEach(btn => {
        btn.addEventListener("click", () => {
          unit = btn.getAttribute("data-hz-mvs-unit") || "cm";
          root.querySelectorAll("[data-hz-mvs-unit]").forEach(b =>
            b.setAttribute("aria-pressed", b === btn ? "true" : "false")
          );
          render();
          forceRecalc();
        });
      });

      forceRecalc();
      return;
    }

    /* =============================
       ACCESSORY (SCARF / TOTE_BAG)
       - type: data-hz-mvs="accessory"
       - selection: data-garment="scarf" or "tote_bag"
    ============================= */

    if (type === "accessory" || type === "accessory-scarf") {
      // Only show the correct garment layer if it exists in the current SVG
      const candidates = ["scarf", "tote_bag"];
      candidates.forEach(id => {
        const el = svg.querySelector(`#${CSS.escape(id)}`);
        if (!el) return;
        el.style.display = (accessoryGarment && normalizeId(id) === accessoryGarment) ? "" : "none";
      });

      // Only length + width for accessories
      showMeasurement(svg, "length", base.a !== null);
      showMeasurement(svg, "width", base.b !== null);

      // Hide anything else that might exist accidentally
      [
        "shoulders_inseam",
        "sleeve_hem",
        "sleeve_long_set_in",
        "sleeve_long_raglan",
        "short_sleeve_set_in",
        "long_sleeve_set_in",
        "short_sleeve_raglan",
        "long_sleeve_raglan",
      ].forEach(id => {
        const el = svg.querySelector(`#${CSS.escape(id)}`);
        if (el) el.style.display = "none";
      });

      function render() {
        setSvgText(svg, "value_length", fmt(base.a));
        setSvgText(svg, "value_width", fmt(base.b));
      }

      render();

      root.querySelectorAll("[data-hz-mvs-unit]").forEach(btn => {
        btn.addEventListener("click", () => {
          unit = btn.getAttribute("data-hz-mvs-unit") || "cm";
          root.querySelectorAll("[data-hz-mvs-unit]").forEach(b =>
            b.setAttribute("aria-pressed", b === btn ? "true" : "false")
          );
          render();
          forceRecalc();
        });
      });

      forceRecalc();
      return;
    }
  }

  /* =============================
     BOOT
  ============================= */

  function boot() {
    const active = getActiveRoot();
    if (active) init(active);
    getAllRoots().forEach(r => init(r));
  }

  document.addEventListener("DOMContentLoaded", boot);
  window.addEventListener("load", boot);

  window.addEventListener("resize", () => {
    const active = getActiveRoot();
    if (active) init(active);
  });

})();
